import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../../../backend/firebaseConfig";
import {
  getIODetailsRef,
  getLegacyIDDetailsRef,
} from "./ioDetailsService";
import { toIdentifierArray } from "./ioIdentifierFormatters";
import {
  normaliseIOImportEmail,
  parseIOImportWorkbook,
} from "./ioDetailsBulkUploadParser";

const ADVISERS_PATH = ["Authorized Members", "Advisers", "Data"];

const parseIdentifier = (value, label) => {
  const text = String(value ?? "").trim();
  if (!text) return { error: `${label} is empty.` };
  if (!/^\d+$/.test(text)) {
    return { error: `${label} must be a whole number.` };
  }

  const number = Number(text);
  if (!Number.isSafeInteger(number)) {
    return { error: `${label} is outside the supported number range.` };
  }

  return { value: number };
};

const parseWorkbookRows = async (file) => {
  return parseIOImportWorkbook(await file.arrayBuffer(), file?.name);
};

export const previewIOAdviserIdImport = async (file) => {
  if (!file) throw new Error("Please choose an Excel file first.");

  const { rows: parsedRows, sheetName } = await parseWorkbookRows(file);
  if (!parsedRows.length) throw new Error("No adviser rows were found.");

  const adviserSnapshot = await getDocs(collection(db, ...ADVISERS_PATH));
  const advisersByEmail = new Map();
  const adviserRecords = adviserSnapshot.docs.map((adviserDocument) => {
    const adviser = adviserDocument.data() || {};
    return {
      adviserEmail: adviserDocument.id,
      storedEmail: adviser.email || adviserDocument.id,
      adviserUserId:
        adviser.adviserUserId ??
        adviser.adviseruserid ??
        adviser.adviUserId ??
        adviser.adviuserid ??
        "",
    };
  });

  adviserRecords.forEach((adviser) => {
    [adviser.storedEmail, adviser.adviserEmail].forEach((emailValue) => {
      const email = normaliseIOImportEmail(emailValue);
      if (email) advisersByEmail.set(email, adviser);
    });
  });

  const currentIODetails = new Map();
  const existingIdOwners = new Map();
  await Promise.all(
    adviserRecords.map(async (adviser) => {
      const [ioDetailsSnapshot, legacyIDDetailsSnapshot] = await Promise.all([
        getDoc(getIODetailsRef(adviser.adviserEmail)),
        getDoc(getLegacyIDDetailsRef(adviser.adviserEmail)),
      ]);
      const ioDetails = ioDetailsSnapshot.exists()
        ? ioDetailsSnapshot.data() || {}
        : {};
      const identifiers = {
        adviserUserId: adviser.adviserUserId,
        ioAdviserId: toIdentifierArray(ioDetails.ioAdviserId),
        id: toIdentifierArray(ioDetails.id),
        groupId: toIdentifierArray(ioDetails.groupId),
        userId: toIdentifierArray(ioDetails.userId),
        hasLegacyIDDetails: legacyIDDetailsSnapshot.exists(),
      };
      currentIODetails.set(adviser.adviserEmail, identifiers);
      identifiers.id.forEach((id) =>
        existingIdOwners.set(String(id), adviser.adviserEmail),
      );
    }),
  );

  const idCounts = new Map();
  parsedRows.forEach((row) => {
    const parsedId = parseIdentifier(row.rawIoAdviserId, "id");
    if (parsedId.value !== undefined) {
      idCounts.set(parsedId.value, (idCounts.get(parsedId.value) || 0) + 1);
    }
  });

  const rows = await Promise.all(
    parsedRows.map(async (row) => {
      const parsedId = parseIdentifier(row.rawIoAdviserId, "id");
      const parsedGroupId = parseIdentifier(row.rawIoGroupId, "group.id");
      const parsedUserId = parseIdentifier(row.rawIoUserId, "user.id");
      const match = advisersByEmail.get(row.email);

      if (!row.email) return { ...row, status: "Invalid", reason: "Email is empty." };
      if (!/^\S+@\S+\.\S+$/.test(row.email)) {
        return { ...row, status: "Invalid", reason: "Email format is invalid." };
      }
      if (parsedId.error) {
        return { ...row, status: "Invalid", reason: parsedId.error };
      }
      if (parsedGroupId.error) {
        return { ...row, status: "Invalid", reason: parsedGroupId.error };
      }
      if (parsedUserId.error) {
        return { ...row, status: "Invalid", reason: parsedUserId.error };
      }
      const identifiers = {
        id: parsedId.value,
        groupId: parsedGroupId.value,
        userId: parsedUserId.value,
      };
      if ((idCounts.get(parsedId.value) || 0) > 1) {
        return { ...row, identifiers, status: "Duplicate", reason: "id appears more than once." };
      }
      if (!match) {
        return { ...row, identifiers, status: "Not found", reason: "No existing adviser matches this email." };
      }

      const existingOwner = existingIdOwners.get(String(parsedId.value));
      if (existingOwner && existingOwner !== match.adviserEmail) {
        return {
          ...row,
          adviserEmail: match.adviserEmail,
          storedEmail: match.storedEmail,
          identifiers,
          currentIdentifiers: currentIODetails.get(match.adviserEmail),
          status: "Conflict",
          reason: `id is already assigned to ${existingOwner}.`,
        };
      }

      const currentIdentifiers = currentIODetails.get(match.adviserEmail) || {};
      const status =
        ["id", "groupId", "userId"].every((key) =>
          toIdentifierArray(currentIdentifiers[key]).includes(
            identifiers[key],
          ),
        ) && !currentIdentifiers.hasLegacyIDDetails
        ? "No change"
        : "Matched";

      return {
        ...row,
        adviserEmail: match.adviserEmail,
        storedEmail: match.storedEmail,
        identifiers,
        currentIdentifiers,
        existingAdviserId: currentIdentifiers.adviserUserId ?? "",
        existingIoAdviserId: currentIdentifiers.ioAdviserId ?? "",
        status,
        reason:
          status === "No change"
            ? "Already saved in IO Details."
            : currentIdentifiers.hasLegacyIDDetails
              ? "Will save to IO Details and remove legacy ID Details."
              : "",
      };
    }),
  );

  const invalidRows = rows.filter(
    (row) => !["Matched", "No change"].includes(row.status),
  );
  const matchedRows = rows.filter((row) => row.status === "Matched");

  return {
    fileName: file.name,
    sheetName,
    totalRows: rows.length,
    rows,
    matchedRows,
    noChangeCount: rows.filter((row) => row.status === "No change").length,
    invalidCount: invalidRows.length,
    valid: invalidRows.length === 0,
  };
};

export const commitIOAdviserIdImport = async (preview) => {
  if (!preview?.valid) {
    throw new Error("Resolve every invalid or unmatched row before importing.");
  }

  const rows = Array.isArray(preview.matchedRows) ? preview.matchedRows : [];
  if (!rows.length) throw new Error("There are no changes to import.");

  const updatedBy = auth.currentUser?.email || "";
  const updatedByName =
    auth.currentUser?.displayName || updatedBy || "Unknown user";
  const chunkSize = 200;
  const adviserUpdates = new Map();

  rows.forEach((row) => {
    const existing = adviserUpdates.get(row.adviserEmail) || {
      adviserEmail: row.adviserEmail,
      currentIdentifiers: row.currentIdentifiers || {},
      identifiers: {
        id: toIdentifierArray(row.currentIdentifiers?.id),
        groupId: toIdentifierArray(row.currentIdentifiers?.groupId),
        userId: toIdentifierArray(row.currentIdentifiers?.userId),
      },
      removeLegacyIDDetails: Boolean(
        row.currentIdentifiers?.hasLegacyIDDetails,
      ),
    };

    ["id", "groupId", "userId"].forEach((fieldPath) => {
      existing.identifiers[fieldPath] = [
        ...new Set([
          ...existing.identifiers[fieldPath],
          row.identifiers[fieldPath],
        ]),
      ];
    });
    adviserUpdates.set(row.adviserEmail, existing);
  });

  const updates = [...adviserUpdates.values()];

  for (let start = 0; start < updates.length; start += chunkSize) {
    const batch = writeBatch(db);

    updates.slice(start, start + chunkSize).forEach((row) => {
      const ioDetailsRef = getIODetailsRef(row.adviserEmail);
      batch.set(
        ioDetailsRef,
        {
          ...row.identifiers,
          updatedAt: serverTimestamp(),
          updatedBy,
          updatedByName,
        },
        { merge: true },
      );

      const logRef = doc(collection(ioDetailsRef, "Edit Logs"));
      batch.set(logRef, {
        adviserEmail: row.adviserEmail,
        changes: ["id", "groupId", "userId"]
          .map((fieldPath) => ({
            fieldPath,
            previousValue: toIdentifierArray(
              row.currentIdentifiers?.[fieldPath],
            ),
            newValue: row.identifiers[fieldPath],
          })),
        changeCount: 3,
        source: "excel-bulk-upload",
        sourceFileName: preview.fileName || "",
        updatedAt: serverTimestamp(),
        updatedBy,
        updatedByName,
        editedAt: serverTimestamp(),
        editedBy: updatedBy,
      });
    });

    await batch.commit();
  }

  for (const update of updates.filter((row) => row.removeLegacyIDDetails)) {
    const legacyRef = getLegacyIDDetailsRef(update.adviserEmail);
    const legacyLogs = await getDocs(collection(legacyRef, "Edit Logs"));

    for (let start = 0; start < legacyLogs.docs.length; start += 400) {
      const deleteBatch = writeBatch(db);
      legacyLogs.docs
        .slice(start, start + 400)
        .forEach((logDocument) => deleteBatch.delete(logDocument.ref));
      await deleteBatch.commit();
    }

    const deleteBatch = writeBatch(db);
    deleteBatch.delete(legacyRef);
    await deleteBatch.commit();
  }

  return { written: updates.length, unchanged: preview.noChangeCount || 0 };
};
